/**
 * frontend/src/pages/valuation/ValuationTuning.tsx — REFACTOR-SPLIT VAL-TUNING-SPLIT-1
 * VALUATION-B3 — methodology tuning screen with live recompute; every change
 * re-runs POST .../recompute and .../preflight (same debounce). Preview
 * flags (out-of-range override comes back stated, flagged, band widened,
 * never clamped — CC#5), finalize fences (nothing is persisted;
 * `persisted: false` on every recompute; record created once, at sign-off).
 */
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getValuationEngagement } from '@/api/valuation'
import type { ValuationEngagement } from '@/types/valuation'
import { TuningControls } from './_TuningControls'
import { TuningSignoffColumn } from './_TuningSignoffColumn'
import { StepFooter } from '@/components/valuation/StepFooter'
import { ValuationStepper, useValuationEntitlement } from './_shared'
import { buildRecomputeBody, type ResolvedIndustry } from './_valuationTuningHelpers'
import { benchmarkKeyForIndustry } from './_valuationIndustryMap'
import { useValuationDraft } from './_valuationDraft'
import { useTuningPreview } from './_useTuningPreview'
import { useTuningInputs } from './_useTuningInputs'
import { isMarketOnlyRun } from './_MarketOnlyAck'
import { useMethodologyEdits } from './_useMethodologyEdits'
import { MethodologyEarnings } from './_MethodologyEarnings'
import { RatesSection } from './_RatesUsed'
import { IntakeMultipleOverride } from './_IntakeMultipleOverride'
import { SetAsideReasons, useSetAsideReasons } from './_SetAsideReasons'
import { ValueMethodsSection } from './_ValueMethodsSection'

export default function ValuationTuning() {
  const { valuationId: id } = useParams<{ valuationId: string }>()
  const { canOverride } = useValuationEntitlement()

  const [loadError, setLoadError] = useState<string | null>(null)

  // VAL-NAV-STATE-1 R2 — industry is chosen once, at engagement creation
  // (ValuationProfileSelect), and displayed read-only here — never picked
  // on this screen. Fetched directly rather than read off the draft, so the
  // value shown is the engagement's own field, not whatever a prior Tuning
  // visit happened to save.
  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  useEffect(() => {
    if (!id) return
    let live = true
    getValuationEngagement(id)
      .then(en => { if (live) setEngagement(en) })
      .catch(() => { if (live) setLoadError('Could not load the engagement.') })
    return () => { live = false }
  }, [id])
  const resolvedIndustry: ResolvedIndustry | null = useMemo(
    () =>
      engagement
        ? { key: benchmarkKeyForIndustry(engagement.industry), naicsCode: null, subVertical: null }
        : null,
    [engagement],
  )

  // VAL-INTAKE-1 D2/D3/D4/D6 — the one working draft, shared with Intake.
  const {
    draft, draftTuning, draftExtras,
    resuming,
    resumedAtLabel,
    savedAtLabel,
    save: saveDraft,
    dismiss: dismissDraft,
  } = useValuationDraft(id)

  const {
    answers,
    knobs, resetKnobs,
    rationale, setRationale,
    acknowledgeMarketOnly, setAcknowledgeMarketOnly, clearMarketOnlyAck,
    config,
    questionnaire,
    baseline,
    tuned,
    startClean,
    onKnob,
  } = useTuningInputs(id, draft, dismissDraft, setLoadError, draftTuning)

  // VAL-PATH-SIGNER P7b — the earnings are shown, never typed: the applied
  // figure (normalization's, or the signer's override) drives the preview.
  const m = useMethodologyEdits(id, draft, draftExtras, saveDraft, canOverride)
  const earningsValue = m.earnings.applied ?? NaN
  const earningsOk = Number.isFinite(earningsValue) && earningsValue > 0
  const rawThreshold = config?.config.earnings_basis_rule?.crossover_earnings_threshold
  const threshold = typeof rawThreshold === 'number' ? rawThreshold : null
  // The signer's edits ride on the preview only once the draft is in.
  const edits = canOverride && m.hydrated
  const answeredAll =
    !!questionnaire &&
    questionnaire.questions.filter(q => q.required).every(q => (answers[q.question_id] ?? '') !== '')
  const ready = !!resolvedIndustry && earningsOk && answeredAll

  /** The one body both /recompute and /preflight are called with. */
  const body = useMemo(() => {
    const built = buildRecomputeBody({
      industry: resolvedIndustry,
      earningsOk,
      earningsValue,
      answers,
      knobs,
      baseline,
      tuned,
      rationale,
      draft,
      acknowledgeMarketOnly,
    })
    return built && edits ? { ...built, ...m.previewFields, basis: m.basis ?? built.basis } : built
  }, [
    resolvedIndustry, earningsOk, earningsValue, answers, knobs, baseline, tuned, rationale,
    draft, acknowledgeMarketOnly, edits, m.previewFields, m.basis,
  ])

  // VAL-INTAKE-BLANK-JUDGMENT-1 C2 — any of the four rates blank (the
  // signer's edited ones once they are on screen).
  const rateSource: { [k: string]: unknown } | null = edits ? { ...m.previewFields } : draft ? { ...draft } : null
  const ratesMissing = [
    rateSource?.risk_free_rate, rateSource?.company_specific_risk,
    rateSource?.long_term_growth_rate, rateSource?.growth_rate,
  ].some(v => v === null || v === undefined)

  // Debounced live recompute + preflight, and (D2) the settled-save trigger.
  const { computed, basis, blockers, warnings, runError, saveError, pending, refresh } = useTuningPreview(
    id,
    ready,
    body,
    saveDraft,
    ratesMissing,
  )

  // R3.11 — the working draft's save is now awaited inside useTuningPreview;
  // this is where the failure actually reaches the valuator, instead of
  // being lost in a fired-and-forgotten promise.
  useEffect(() => {
    if (saveError) toast.error(saveError)
  }, [saveError])

  // D6 — exact match on the engine's own wording (valuation_run.py); this is
  // the one case where "not triangulated" is a decision, not a footnote.
  const isMarketOnly = isMarketOnlyRun(computed)
  // POL-B item 1 — a reason per approach set aside, saved with the draft.
  const setAside = useSetAsideReasons(m.hydrated, draftExtras, saveDraft, canOverride, computed?.set_aside)
  useEffect(() => {
    if (!isMarketOnly) clearMarketOnlyAck()
  }, [isMarketOnly])

  if (loadError) {
    return (
      <div className="p-6">
        <p className="rounded border border-pal-error-border bg-pal-error-bg px-3 py-2 text-sm text-pal-error-text">
          {loadError}
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <ValuationStepper current="tuning" engagementId={id} />

      <header className="mb-6">
        {/* OWNER_APPROVALS.log 10-20 22:42 — signer step 'Methodology and rates'. */}
        <h1 className="text-xl font-semibold text-pal-navy">Methodology and rates</h1>
        <p className="mt-1 text-sm text-pal-ink-soft">
          Every number on the right came back from the engine on the current settings.
          Your inputs are saved as a working draft as you go — the signed-off record
          is still created once, at sign-off.
        </p>
        {resuming && (
          <p className="mt-2 flex flex-wrap items-center gap-2 rounded border border-pal-info-border bg-pal-info-bg px-3 py-2 text-sm text-blue-900">
            <span>Resuming your draft from {resumedAtLabel}.</span>
            <button
              type="button"
              onClick={startClean}
              className="font-medium underline underline-offset-2 hover:text-pal-link"
            >
              Start clean
            </button>
          </p>
        )}
        {!resuming && savedAtLabel && (
          <p className="mt-2 text-xs text-pal-ink-muted">{savedAtLabel}</p>
        )}
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Industry</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                {engagement ? (
                  <p className="text-sm text-pal-ink-body">{engagement.industry_label ?? engagement.industry ?? 'Not set'}</p>
                ) : (
                  <Skeleton className="h-4 w-32" />
                )}
                <p className="mt-1 text-xs text-pal-ink-muted">
                  Set once at engagement creation — not editable here.
                </p>
              </div>
            </CardContent>
          </Card>

          <MethodologyEarnings earnings={m.earnings} editable={edits} threshold={threshold} />

          {m.hydrated && (
            <RatesSection
              values={m.rates}
              industry={engagement?.industry}
              edit={edits ? m.editRate : undefined}
            />
          )}
          {m.ratesMessage && <p className="text-xs text-red-700">{m.ratesMessage}</p>}
          {edits && <IntakeMultipleOverride {...m.multipleOverride} />}

          <Card>
            <CardHeader>
              <CardTitle>Tunable parameters</CardTitle>
            </CardHeader>
            <CardContent>
              {config && Object.keys(knobs).length > 0 ? (
                <TuningControls
                  values={knobs}
                  baseline={baseline}
                  marketWeight={knobs.market_weight ?? baseline.market_weight}
                  rationale={rationale}
                  tuned={tuned}
                  disabled={!canOverride}
                  onChange={onKnob}
                  onRationale={setRationale}
                  onReset={() => {
                    resetKnobs(baseline)
                    setRationale('')
                  }}
                />
              ) : (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="space-y-1">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  ))}
                </div>
              )}
              {!canOverride && (
                <p className="mt-3 text-xs text-pal-ink-muted">
                  Your role can view the methodology but not tune it.
                </p>
              )}
            </CardContent>
          </Card>

          {edits && <SetAsideReasons r={setAside} />}

          {/* QA-20 — one read-only line; no version list, no Activate. */}
        </div>

        <TuningSignoffColumn
          valueKind={engagement?.value_kind}
          computed={computed}
          basis={basis}
          pending={pending}
          runError={runError}
          blockers={blockers}
          warnings={warnings}
          isMarketOnly={isMarketOnly}
          acknowledgeMarketOnly={acknowledgeMarketOnly}
          onAcknowledgeMarketOnly={setAcknowledgeMarketOnly}
        />
      </div>
      {/* M-DEMO — Compare values + the 10-year Forecast; screen only. */}
      {computed?.value_methods && id && (
        <ValueMethodsSection
          engagementId={id}
          payload={computed.value_methods}
          signer={canOverride}
          stored={draftExtras.value_methods}
          onSaved={refresh}
        />
      )}
      {/* Sign-off reads the saved draft (saved on every settled recompute,
          D2); nothing is handed forward from here. */}
      <StepFooter
        step="tuning"
        engagementId={id ?? ''}
        nextDisabled={!computed || blockers.length > 0 || (isMarketOnly && !acknowledgeMarketOnly)}
        note={blockers.length > 0 ? `Sign-off blocked (${blockers.length})` : null}
      />
    </div>
  )
}
